/**
 * Extracts a human-readable string from a backend error payload.
 *
 * The Django backend's standard envelope (apps/payments/exceptions.py,
 * custom_exception_handler) is:
 *   { "error": { "code": "...", "message": "...", "details": {}, ... } }
 *
 * Never render the raw payload (or the nested `error`/`details` object)
 * directly as UI text — it's an object, not a string, and passing it to
 * something that renders it as a React child (e.g. a toast) crashes with
 * "Objects are not valid as a React child".
 */
export function extractApiErrorMessage(
  data: unknown,
  fallback: string,
): string {
  if (typeof data === "string" && data.trim()) return data;
  if (!data || typeof data !== "object") return fallback;

  const obj = data as Record<string, unknown>;

  // { error: "..." } or { error: { message: "..." } }
  if (typeof obj.error === "string" && obj.error.trim()) return obj.error;
  if (obj.error && typeof obj.error === "object") {
    const nested = obj.error as Record<string, unknown>;
    if (typeof nested.message === "string" && nested.message.trim()) {
      return nested.message;
    }
  }

  // Flat { message: "..." } or DRF-default { detail: "..." }
  if (typeof obj.message === "string" && obj.message.trim()) return obj.message;
  if (typeof obj.detail === "string" && obj.detail.trim()) return obj.detail;

  // DRF field-validation errors: { field: ["msg", ...] }
  if (Array.isArray(obj.detail) && obj.detail.length > 0) {
    const first = obj.detail[0];
    if (typeof first === "string") return first;
    if (first && typeof first === "object" && typeof (first as Record<string, unknown>).msg === "string") {
      return (first as Record<string, unknown>).msg as string;
    }
  }

  return fallback;
}
